import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const refCode = (params.get("ref") || (typeof window !== "undefined" ? window.localStorage.getItem("caseirinho_ref") : "") || "").toUpperCase();
    const isProducerCode = refCode.startsWith("CAS-PROD");
    const initialRole = params.get("role") === "vendor" || isProducerCode ? "vendor" : "customer";
    const [role, setRole] = useState(initialRole);
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        producer_name: "",
        region: "",
        story: "",
        referral_code: refCode,
    });
    const [busy, setBusy] = useState(false);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        const payload = { ...form, role };
        if (role !== "vendor") {
            delete payload.producer_name;
            delete payload.region;
            delete payload.story;
        }
        const res = await register(payload);
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success(role === "vendor" ? "Conta criada — a aguardar aprovação" : "Bem-vindo ao Caseirinho");
        if (role === "vendor") navigate("/vendor");
        else navigate("/conta");
    }

    return (
        <>
            <Navbar variant="light" />
            <section className="pt-32 pb-24 bg-beige-natural">
                <div className="container-edge max-w-xl mx-auto">
                    <span className="label-overline text-wine">Conta</span>
                    <h1 className="mt-4 font-heading text-5xl text-wine-dark">Criar conta</h1>

                    <div className="mt-8 inline-flex border border-wine-dark/20" data-testid="role-tabs">
                        {[
                            ["customer", "Cliente"],
                            ["vendor", "Produtor"],
                        ].map(([k, label]) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setRole(k)}
                                className={`label-overline px-6 py-3 transition-colors ${role === k ? "bg-wine text-beige-natural" : "text-wine-dark hover:bg-wine/5"}`}
                                data-testid={`role-${k}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
                        <Field label={role === "vendor" ? "Nome do responsável" : "Nome completo"} value={form.name} onChange={(v) => set("name", v)} testId="reg-name" />
                        <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="reg-email" />
                        <Field label="Palavra-passe" type="password" value={form.password} onChange={(v) => set("password", v)} testId="reg-password" />

                        {role !== "vendor" && refCode && (
                            <div className="bg-vineyard/10 border border-vineyard/30 rounded-md px-4 py-3" data-testid="customer-referral-note">
                                <span className="label-overline text-vineyard text-[10px]">Convite de amigo</span>
                                <p className="text-sm text-wine-dark/80 mt-0.5">Foi convidado com o código <span className="font-mono text-wine">{refCode}</span> — vai receber desconto na sua primeira compra. 🎁</p>
                            </div>
                        )}

                        {role === "vendor" && (
                            <>
                                <Field label="Nome do produtor / quinta" value={form.producer_name} onChange={(v) => set("producer_name", v)} testId="reg-producer-name" />
                                <Field label="Região" value={form.region} onChange={(v) => set("region", v)} testId="reg-region" placeholder="Douro, Alentejo, Dão…" />
                                <label className="block">
                                    <span className="label-overline text-wine-dark">Código do Produtor Indicador <span className="opacity-60">(opcional)</span></span>
                                    <input
                                        type="text"
                                        value={form.referral_code}
                                        onChange={(e) => set("referral_code", e.target.value.toUpperCase())}
                                        placeholder="CAS-PROD-00000"
                                        className="mt-2 w-full bg-transparent border border-wine-dark/20 px-4 py-3 text-sm focus:border-wine font-mono uppercase"
                                        data-testid="reg-referral-code"
                                    />
                                    {refCode && (
                                        <span className="text-xs text-vineyard mt-1 block">Chegou através de uma indicação — código preenchido automaticamente.</span>
                                    )}
                                </label>
                                <label className="block">
                                    <span className="label-overline text-wine-dark">A nossa história</span>
                                    <textarea
                                        value={form.story}
                                        onChange={(e) => set("story", e.target.value)}
                                        rows={4}
                                        className="mt-2 w-full bg-transparent border border-wine-dark/20 px-4 py-3 text-sm focus:border-wine"
                                        data-testid="reg-story"
                                    />
                                </label>
                                <p className="text-xs text-wine-dark/60">
                                    A sua candidatura será revista pela equipa Caseirinho antes da publicação dos produtos.
                                </p>
                            </>
                        )}

                        <button disabled={busy} className="btn-wine w-full" data-testid="register-submit">
                            {busy ? "A criar…" : "Criar conta"}
                        </button>
                    </form>

                    <div className="mt-8 text-sm text-wine-dark/70">
                        Já tem conta?{" "}
                        <Link to="/login" className="text-wine font-semibold hover:text-gold-soft">Entrar</Link>
                    </div>
                </div>
            </section>
            <Footer />
        </>
    );
}

function Field({ label, type = "text", value, onChange, testId, placeholder }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark">{label}</span>
            <input
                type={type}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                required
                className="mt-2 w-full bg-transparent border border-wine-dark/20 px-4 py-3 text-sm focus:border-wine"
                data-testid={testId}
            />
        </label>
    );
}
